import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/layout/footer';
import { useLanguage } from '../context/LanguageContext';
import {
  getProductsByCategorySlug,
  getProductShotsByProductIds,
  getFirstImageByProductIds,
  type MaisonAdjiProduct,
  type ProductShot,
} from '../lib/maison-adji-api';

function BrandBlock() {
  const { t } = useLanguage();
  return (
    <div className="w-full h-full min-h-[280px] bg-neutral-950 flex flex-col items-center justify-center gap-6 p-10 md:p-16 border border-white/5">
      <img
        src="/assets/images/maisonadjiwhite.png"
        alt="Maison Adji"
        className="h-14 md:h-20 w-auto object-contain opacity-90"
      />
      <p className="font-['Sora'] text-xs md:text-sm tracking-[0.35em] text-white/50 uppercase text-center max-w-[280px]">
        {t('home.subtitle')}
      </p>
      <p className="font-['Rubik'] text-sm text-white/30 text-center max-w-[260px] leading-relaxed">
        {t('home.tagline')}
      </p>
    </div>
  );
}

function ShotTile({
  product,
  shots,
  fallbackUrl,
}: {
  product: MaisonAdjiProduct;
  shots: ProductShot[];
  fallbackUrl?: string;
}) {
  const { localize } = useLanguage();
  const [hoverIndex, setHoverIndex] = useState(0);
  const [hovering, setHovering] = useState(false);
  const displayName = localize(product, 'name') || product.name;
  const hasShots = shots.length > 0;
  const displayShots = hasShots ? shots : fallbackUrl ? [{ id: 'fallback', url: fallbackUrl, alt_text: null, alt_text_en: null, caption: null, caption_en: null, position: 0 }] : [];

  useEffect(() => {
    if (displayShots.length <= 1 || !hovering) return;
    const t = setInterval(() => setHoverIndex((i) => (i + 1) % displayShots.length), 1800);
    return () => clearInterval(t);
  }, [displayShots.length, hovering]);

  if (displayShots.length === 0) return null;

  return (
    <Link
      to={`/product/${product.slug}`}
      className="group relative block aspect-[3/4] md:aspect-[4/5] overflow-hidden bg-neutral-900"
      onMouseEnter={() => {
        setHovering(true);
        setHoverIndex((i) => (displayShots.length > 1 ? (i + 1) % displayShots.length : 0));
      }}
      onMouseLeave={() => { setHovering(false); setHoverIndex(0); }}
    >
      {displayShots.map((shot, i) => (
        <img
          key={shot.id}
          src={shot.url}
          alt={localize(shot, 'alt_text') || displayName}
          className={[
            'absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500',
            i === hoverIndex ? 'opacity-100 z-10' : 'opacity-0 z-0',
          ].join(' ')}
        />
      ))}
      <div className="absolute inset-0 z-20 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 z-20 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
        <p className="font-['Sora'] text-sm text-white truncate">{displayName}</p>
      </div>
    </Link>
  );
}

export default function BespokePage() {
  const [products, setProducts] = useState<MaisonAdjiProduct[]>([]);
  const [shotsByProduct, setShotsByProduct] = useState<Record<string, ProductShot[]>>({});
  const [fallbackImages, setFallbackImages] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const list = await getProductsByCategorySlug('bespoke');
      setProducts(list);
      if (list.length > 0) {
        const ids = list.map((p) => p.id);
        const [shots, firstImgs] = await Promise.all([
          getProductShotsByProductIds(ids),
          getFirstImageByProductIds(ids),
        ]);
        setShotsByProduct(shots);
        setFallbackImages(firstImgs);
      }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-950">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  const items: ('brand' | MaisonAdjiProduct)[] = ['brand', ...products];

  return (
    <div className="bg-neutral-950 min-h-screen">
      <div className="w-full grid grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
        {items.map((item) =>
          item === 'brand' ? (
            <div key="brand" className="w-full h-full min-h-[280px]">
              <BrandBlock />
            </div>
          ) : (
            <ShotTile
              key={item.id}
              product={item}
              shots={shotsByProduct[item.id] ?? []}
              fallbackUrl={fallbackImages[item.id]}
            />
          )
        )}
      </div>
      <Footer />
    </div>
  );
}
